/**
 * Hook متكامل لحفظ الباقات السياحية مع ربط كامل بقاعدة البيانات
 * يشمل: الفنادق، الرحلات، النقل، الجولات، الشمول، الاستثناءات
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { PackageData } from '@/pages/CreatePackage';
import type {
  TourismPackage,
  PackageDetails,
  PackageHotelBooking,
  PackageInclusion,
  PackageExclusion,
  PackageBooking,
  SavePackageResult
} from '@/types/package-database';

export const usePackageSave = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);

  /**
   * توليد رقم الباقة الفريد
   */
  const generatePackageNumber = () => {
    const now = new Date();
    const year = now.getFullYear().toString().slice(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PKG-${year}${month}${day}-${random}`;
  };

  /**
   * البحث عن العميل أو إنشاءه
   */
  const handleClient = async (data: PackageData): Promise<string | null> => {
    if (!data.clientName) return null;

    try {
      // البحث عن العميل الموجود
      const { data: existingClient } = await supabase
        .from('clients')
        .select('id')
        .eq('name_ar', data.clientName)
        .maybeSingle();

      if (existingClient) {
        return existingClient.id;
      }

      // إنشاء عميل جديد إذا لم يكن موجوداً
      const clientData: any = {
        name_ar: data.clientName,
        mobile: data.clientPhone || null,
        company: data.companyName || null,
        status: 'waiting',
        client_type: data.clientType === 'b2b' ? 'b2b' : 'direct'
      };

      if (data.clientEmail) clientData.name_en = data.clientEmail;
      if (data.companyId) clientData.company_id = data.companyId;

      const { data: newClient, error } = await supabase
        .from('clients')
        .insert(clientData)
        .select('id')
        .single();

      if (error) {
        console.error('Error creating client:', error);
        return null;
      }

      return newClient.id;
    } catch (error) {
      console.error('Error handling client:', error);
      return null;
    }
  };

  /**
   * الحصول على معرف الموظف الحالي
   */
  const getCurrentEmployeeId = async (): Promise<string | null> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: employee } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      return employee?.id || null;
    } catch (error) {
      console.error('Error getting employee ID:', error);
      return null;
    }
  };

  /**
   * حفظ الباقة الأساسية في tourism_packages
   */
  const saveTourismPackage = async (
    data: PackageData,
    clientId: string | null,
    employeeId: string | null,
    status: 'draft' | 'active'
  ): Promise<TourismPackage | null> => {
    try {
      const packageNumber = generatePackageNumber();

      const packageData = {
        package_number: packageNumber,
        title: data.packageName || 'عرض سياحي',
        destination: data.destination || 'غير محدد',
        duration: data.duration || 'غير محدد',
        price: data.totalPrice || 0,
        currency: 'SAR' as const,
        status: status as 'draft' | 'active',
        payment_status: 'pending' as const,
        type: (data.clientType === 'b2b' ? 'b2b' : 'employee') as 'b2b' | 'employee',
        client_name: data.clientName,
        description: data.notes || null,
        package_details: JSON.parse(JSON.stringify(data)), // حفظ كل البيانات كـ JSONB
        ...(clientId && { client_id: clientId }),
        ...(employeeId && { created_by: employeeId })
      };

      const { data: savedPackage, error } = await supabase
        .from('tourism_packages')
        .insert([packageData])
        .select()
        .single();

      if (error) throw error;

      return savedPackage as TourismPackage;
    } catch (error) {
      console.error('Error saving tourism package:', error);
      throw error;
    }
  };

  /**
   * حفظ تفاصيل الباقة في package_details
   */
  const savePackageDetails = async (
    packageId: string,
    data: PackageData
  ): Promise<PackageDetails | null> => {
    try {
      const detailsData: any = {
        package_id: packageId,
        adults_count: data.adultsCount || 0,
        children_count: data.childrenCount || 0,
        infants_count: data.infantsCount || 0,
        rooms_count: data.numberOfRooms || 0,
        nights_count: data.numberOfNights || 0,
        meal_plans: data.mealPlans || [],
        cities_plan: data.citiesPlan || []
      };

      // حساب التكاليف
      const hotelsCost = data.hotels?.reduce((sum, h) => sum + (h.pricePerNight * h.nights || 0), 0) || 0;
      const flightsCost = data.flights?.reduce((sum, f) => sum + (f.price || 0), 0) || 0;
      const transportCost = data.transport?.reduce((sum, t) => sum + (t.price || 0), 0) || 0;
      const toursCost = data.tours?.reduce((sum, t) => sum + (t.price || 0), 0) || 0;
      const servicesCost = data.services?.reduce((sum, s) => sum + (s.price || 0), 0) || 0;

      detailsData.hotels_cost = hotelsCost;
      detailsData.flights_cost = flightsCost;
      detailsData.transport_cost = transportCost;
      detailsData.tours_cost = toursCost;
      detailsData.services_cost = servicesCost;

      const { data: savedDetails, error } = await supabase
        .from('package_details')
        .insert(detailsData)
        .select()
        .single();

      if (error) throw error;

      return savedDetails;
    } catch (error) {
      console.error('Error saving package details:', error);
      return null;
    }
  };

  /**
   * حفظ حجوزات الفنادق في package_hotel_bookings
   */
  const saveHotelBookings = async (
    packageId: string,
    data: PackageData
  ): Promise<PackageHotelBooking[]> => {
    if (!data.hotels || data.hotels.length === 0) return [];

    try {
      const hotelBookings = data.hotels.map((hotel) => ({
        package_id: packageId,
        hotel_id: hotel.name || null,  // استخدام اسم الفندق بدلاً من معرف غير موجود
        check_in: hotel.checkIn || '',
        check_out: hotel.checkOut || '',
        nights: hotel.nights || 0,
        room_id: hotel.roomType || null,
        meal_plan: 'none',
        adults: data.adultsCount || 0,
        children: data.childrenCount || 0
      }));

      const { data: savedBookings, error } = await supabase
        .from('package_hotel_bookings')
        .insert(hotelBookings)
        .select();

      if (error) {
        console.error('Error saving hotel bookings:', error);
        return [];
      }

      return savedBookings || [];
    } catch (error) {
      console.error('Error saving hotel bookings:', error);
      return [];
    }
  };

  /**
   * حفظ ما يشمله العرض في package_inclusions
   */
  const saveInclusions = async (
    packageId: string,
    inclusions: any[]
  ): Promise<any[]> => {
    if (!inclusions || inclusions.length === 0) return [];

    try {
      const inclusionsData = inclusions.map((item) => ({
        text_ar: typeof item === 'string' ? item : item.text_ar || '',
        text_en: typeof item === 'string' ? item : item.text_en || ''
      }));

      const { data: savedInclusions, error } = await supabase
        .from('package_inclusions')
        .insert(inclusionsData)
        .select();

      if (error) {
        console.error('Error saving inclusions:', error);
        return [];
      }

      return savedInclusions || [];
    } catch (error) {
      console.error('Error saving inclusions:', error);
      return [];
    }
  };

  /**
   * حفظ ما لا يشمله العرض في package_exclusions
   */
  const saveExclusions = async (
    packageId: string,
    exclusions: any[]
  ): Promise<any[]> => {
    if (!exclusions || exclusions.length === 0) return [];

    try {
      const exclusionsData = exclusions.map((item) => ({
        text_ar: typeof item === 'string' ? item : item.text_ar || '',
        text_en: typeof item === 'string' ? item : item.text_en || ''
      }));

      const { data: savedExclusions, error } = await supabase
        .from('package_exclusions')
        .insert(exclusionsData)
        .select();

      if (error) {
        console.error('Error saving exclusions:', error);
        return [];
      }

      return savedExclusions || [];
    } catch (error) {
      console.error('Error saving exclusions:', error);
      return [];
    }
  };

  /**
   * حفظ الرحلات الجوية في package_flights
   */
  const saveFlights = async (
    packageId: string,
    flights: any[]
  ): Promise<any[]> => {
    if (!flights || flights.length === 0) return [];

    try {
      const flightsData = flights.map((flight) => ({
        package_id: packageId,
        flight_type: flight.type === 'outbound' || flight.type === 'return' ? 'international' : 'domestic',
        direction: flight.type || 'outbound',
        airline_name: flight.airline || '',
        from_airport_name: flight.from || '',
        from_country: flight.fromCountry || '',
        from_city: flight.fromCity || '',
        to_airport_name: flight.to || '',
        to_country: flight.toCountry || '',
        to_city: flight.toCity || '',
        flight_date: flight.date || new Date().toISOString().split('T')[0],
        flight_time: flight.time || null,
        flight_class: flight.class || 'economy',
        adults_count: flight.adults || 0,
        children_count: flight.children || 0,
        infants_count: flight.infants || 0,
        is_transit: flight.transitFlight || false,
        transit_duration: flight.transitDuration || null,
        cost_price: flight.costPrice || 0,
        selling_price: flight.sellingPrice || flight.price || 0,
        total_price: flight.price || 0,
        currency: 'SAR',
        supplier: flight.supplier || null,
        notes: flight.notes || null
      }));

      const { data: savedFlights, error } = await supabase
        .from('package_flights')
        .insert(flightsData)
        .select();

      if (error) {
        console.error('Error saving flights:', error);
        return [];
      }

      return savedFlights || [];
    } catch (error) {
      console.error('Error saving flights:', error);
      return [];
    }
  };

  /**
   * حفظ النقل والمواصلات في package_transport
   */
  const saveTransport = async (
    packageId: string,
    transport: any[]
  ): Promise<any[]> => {
    if (!transport || transport.length === 0) return [];

    try {
      const transportData = transport.map((item) => ({
        package_id: packageId,
        transport_type: item.type || 'airport_transfer',
        vehicle_type_name: item.vehicle || '',
        from_location: item.from || '',
        to_location: item.to || '',
        transport_date: item.date || new Date().toISOString().split('T')[0],
        cost_price: 0,
        selling_price: item.price || 0,
        total_price: item.price || 0,
        currency: 'SAR',
        notes: null
      }));

      const { data: savedTransport, error } = await supabase
        .from('package_transport')
        .insert(transportData)
        .select();

      if (error) {
        console.error('Error saving transport:', error);
        return [];
      }

      return savedTransport || [];
    } catch (error) {
      console.error('Error saving transport:', error);
      return [];
    }
  };

  /**
   * حفظ الجولات السياحية في package_tours
   */
  const saveTours = async (
    packageId: string,
    tours: any[]
  ): Promise<any[]> => {
    if (!tours || tours.length === 0) return [];

    try {
      const toursData = tours.map((tour) => ({
        package_id: packageId,
        tour_name: tour.name || '',
        description: tour.description || '',
        tour_date: tour.date || new Date().toISOString().split('T')[0],
        duration: tour.duration || '',
        cost_price: 0,
        selling_price: tour.price || 0,
        total_price: tour.price || 0,
        currency: 'SAR',
        included_services: tour.included || [],
        notes: null
      }));

      const { data: savedTours, error } = await supabase
        .from('package_tours')
        .insert(toursData)
        .select();

      if (error) {
        console.error('Error saving tours:', error);
        return [];
      }

      return savedTours || [];
    } catch (error) {
      console.error('Error saving tours:', error);
      return [];
    }
  };

  /**
   * إنشاء سجل حجز في package_bookings
   */
  const createPackageBooking = async (
    packageId: string,
    packageNumber: string,
    clientId: string | null,
    employeeId: string | null,
    data: PackageData
  ): Promise<PackageBooking | null> => {
    try {
      const bookingData: any = {
        package_id: packageId,
        package_number: packageNumber,
        booking_date: new Date().toISOString().split('T')[0],
        price: data.totalPrice || 0,
        currency: 'SAR',
        status: 'pending',
        payment_status: 'unpaid',
        notes: `${data.packageName || 'عرض سياحي'} - ${data.destination || ''}`
      };

      if (clientId) bookingData.client_id = clientId;
      if (employeeId) {
        bookingData.employee_id = employeeId;
        bookingData.created_by = employeeId;
      }

      const { data: savedBooking, error } = await supabase
        .from('package_bookings')
        .insert(bookingData)
        .select()
        .single();

      if (error) throw error;

      return savedBooking;
    } catch (error) {
      console.error('Error creating package booking:', error);
      return null;
    }
  };

  /**
   * إنشاء مهمة متابعة تلقائية
   */
  const createFollowUpTask = async (
    clientId: string,
    employeeId: string,
    packageId: string
  ): Promise<void> => {
    try {
      // إنشاء متابعة بعد 3 أيام
      const followUpDate = new Date();
      followUpDate.setDate(followUpDate.getDate() + 3);

      await supabase.from('client_follow_ups').insert({
        client_id: clientId,
        employee_id: employeeId,
        related_package_id: packageId,
        follow_up_type: 'package_followup',
        scheduled_date: followUpDate.toISOString(),
        status: 'pending',
        priority: 'normal',
        notes: 'متابعة تلقائية لعرض الباقة السياحية'
      });
    } catch (error) {
      console.error('Error creating follow-up task:', error);
    }
  };

  /**
   * إرسال إشعار للموظف
   */
  const createNotification = async (
    employeeId: string,
    packageId: string,
    packageNumber: string
  ): Promise<void> => {
    try {
      await supabase.from('notifications').insert({
        employee_id: employeeId,
        message: `تم إنشاء باقة سياحية جديدة برقم ${packageNumber}`,
        link: `/tourism-packages`,
        notification_type: 'package_created',
        priority: 'normal'
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  /**
   * إنشاء مهمة متابعة للباقة
   */
  const createPackageTask = async (
    packageId: string,
    packageNumber: string,
    clientName: string,
    employeeId: string
  ): Promise<void> => {
    try {
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 7); // مهمة متابعة بعد أسبوع

      await supabase.from('tasks').insert({
        title: `متابعة الباقة ${packageNumber}`,
        description: `متابعة مع العميل ${clientName} بخصوص الباقة السياحية`,
        assigned_to: employeeId,
        created_by: employeeId,
        due_date: dueDate.toISOString(),
        priority: 'medium',
        status: 'pending',
        type: 'follow_up'
      });
    } catch (error) {
      console.error('Error creating package task:', error);
    }
  };

  /**
   * الوظيفة الرئيسية للحفظ المتكامل
   */
  const savePackage = async (
    data: PackageData,
    status: 'draft' | 'active' = 'draft'
  ): Promise<SavePackageResult> => {
    setIsSaving(true);
    setSaveProgress(0);

    try {
      // التحقق من تسجيل الدخول
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('يجب تسجيل الدخول أولاً');
      }

      setSaveProgress(10);

      // 1. معالجة العميل
      const clientId = await handleClient(data);
      setSaveProgress(20);

      // 2. الحصول على معرف الموظف
      const employeeId = await getCurrentEmployeeId();
      setSaveProgress(30);

      // 3. حفظ الباقة الأساسية
      const savedPackage = await saveTourismPackage(data, clientId, employeeId, status);
      if (!savedPackage || !savedPackage.id) {
        throw new Error('فشل في حفظ الباقة');
      }
      setSaveProgress(40);

      const packageId = savedPackage.id;
      const packageNumber = savedPackage.package_number || '';

      // 4. حفظ التفاصيل
      const packageDetails = await savePackageDetails(packageId, data);
      setSaveProgress(50);

      // 5. حفظ حجوزات الفنادق
      const hotelBookings = await saveHotelBookings(packageId, data);
      setSaveProgress(60);

      // 6. حفظ الشمول
      const savedInclusions = await saveInclusions(packageId, data.inclusions || []);
      setSaveProgress(60);

      // 7. حفظ الاستثناءات
      const savedExclusions = await saveExclusions(packageId, data.exclusions || []);
      setSaveProgress(65);

      // 8. حفظ الرحلات الجوية
      const savedFlights = await saveFlights(packageId, data.flights || []);
      setSaveProgress(70);

      // 9. حفظ النقل
      const savedTransport = await saveTransport(packageId, data.transport || []);
      setSaveProgress(75);

      // 10. حفظ الجولات
      const savedTours = await saveTours(packageId, data.tours || []);
      setSaveProgress(80);

      // 11. إنشاء سجل حجز
      const booking = await createPackageBooking(packageId, packageNumber, clientId, employeeId, data);
      setSaveProgress(90);

      // 12. إنشاء مهمة متابعة (فقط للباقات المنشورة)
      if (status === 'active' && clientId && employeeId) {
        await createFollowUpTask(clientId, employeeId, packageId);
      }
      setSaveProgress(92);

      // 13. إرسال إشعار للموظف المسؤول
      if (status === 'active' && employeeId) {
        await createNotification(employeeId, packageId, packageNumber);
      }
      setSaveProgress(96);

      // 14. إنشاء مهمة عمل للباقة
      if (status === 'active' && employeeId) {
        await createPackageTask(packageId, packageNumber, data.clientName, employeeId);
      }

      setSaveProgress(100);

      toast.success(
        status === 'draft' 
          ? '✅ تم حفظ الباقة كمسودة بنجاح' 
          : '✅ تم نشر الباقة وربطها مع جميع الأنظمة'
      );

      return {
        success: true,
        packageId,
        packageNumber,
        details: {
          package: savedPackage,
          packageDetails: packageDetails || undefined,
          hotelBookings,
          inclusions: savedInclusions,
          exclusions: savedExclusions,
          booking: booking || undefined
        }
      };
    } catch (error: any) {
      console.error('Error in savePackage:', error);
      toast.error('❌ ' + (error.message || 'حدث خطأ أثناء حفظ الباقة'));
      
      return {
        success: false,
        error: error.message || 'حدث خطأ غير معروف'
      };
    } finally {
      setIsSaving(false);
      setSaveProgress(0);
    }
  };

  return {
    savePackage,
    isSaving,
    saveProgress
  };
};
